/* Native forms remain usable without JavaScript. No external dependencies. */
(() => {
  'use strict';
  const readJSON = (node, fallback = []) => { try { return JSON.parse(node?.textContent || ''); } catch { return fallback; } };
  const route = (key, fallback) => window.routes?.[key] || `${window.Shopify?.routes?.root || '/'}${fallback}`;
  const strings = () => ({ add: window.variantStrings?.addToCart || 'In den Warenkorb', sold: window.variantStrings?.soldOut || 'Ausverkauft' });

  class AcHeader extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('click', event => {
        if (event.target.closest('.ac-mobile-menu a')) this.querySelector('.ac-mobile-menu')?.removeAttribute('open');
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const opened = [...this.querySelectorAll('details[open]')];
        opened.forEach(item => item.removeAttribute('open'));
        opened[0]?.querySelector('summary')?.focus();
      }, { signal });
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.querySelectorAll('details[open]').forEach(item => item.removeAttribute('open'));
      }, { signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class AcProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.ac-product-form');
      this.variants = readJSON(this.querySelector('[data-variants]'));
      if (!this.form || !this.variants.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.variantInput = this.form.querySelector('[name="id"]');
      this.planInput = this.form.querySelector('[name="selling_plan"]');
      this.quantity = this.form.querySelector('[name="quantity"]');
      this.error = this.querySelector('[data-product-error]');
      this.status = this.querySelector('[data-product-status]');
      this.busy = false;
      this.updateVariant(false);
      this.variantInput.addEventListener('change', () => this.updateVariant(true), { signal });
      this.planInput?.addEventListener('change', () => this.updatePrice(), { signal });
      this.form.addEventListener('submit', event => this.addToCart(event), { signal });
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-media-target]');
        if (thumb) this.selectMedia(thumb.dataset.mediaTarget);
        const quantityButton = event.target.closest('[data-quantity]');
        if (quantityButton && this.quantity) {
          const step = Number(this.quantity.step) || 1;
          const min = Number(this.quantity.min) || 1;
          const max = this.quantity.max === '' ? Infinity : Number(this.quantity.max);
          const current = Number(this.quantity.value) || min;
          this.quantity.value = String(Math.max(min, Math.min(max, current + (quantityButton.dataset.quantity === 'plus' ? step : -step))));
          this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, { signal });
      if ('IntersectionObserver' in window && this.querySelector('.ac-sticky-buy')) {
        this.stickyObserver = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.querySelector('.ac-sticky-buy').hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        }, { threshold: 0 });
        this.stickyObserver.observe(this.form);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.stickyObserver?.disconnect(); }
    get selectedVariant() { return this.variants.find(variant => String(variant.id) === this.variantInput.value); }
    selectMedia(id) {
      if (!id) return;
      const slides = [...this.querySelectorAll('[data-media-id]')];
      if (!slides.some(slide => slide.dataset.mediaId === String(id))) return;
      slides.forEach(slide => {
        slide.hidden = slide.dataset.mediaId !== String(id);
        if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-media-target]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(id))));
    }
    updateVariant(updateURL) {
      const variant = this.selectedVariant;
      if (!variant) return;
      if (this.quantity) {
        this.quantity.min = String(variant.min || 1);
        this.quantity.step = String(variant.step || 1);
        if (variant.max != null) this.quantity.max = String(variant.max); else this.quantity.removeAttribute('max');
        let quantity = Math.max(variant.min || 1, Number(this.quantity.value) || 1);
        quantity = (variant.min || 1) + Math.ceil((quantity - (variant.min || 1)) / (variant.step || 1)) * (variant.step || 1);
        if (variant.max != null) quantity = Math.min(quantity, variant.max);
        this.quantity.value = String(quantity);
      }
      if (this.planInput) {
        [...this.planInput.options].forEach(option => { option.disabled = !!option.value && !variant.plans.some(plan => String(plan.id) === option.value); });
        const option = this.planInput.selectedOptions[0];
        if (option?.disabled) this.planInput.value = [...this.planInput.options].find(item => !item.disabled)?.value || '';
      }
      this.setError('');
      this.updatePrice();
      if (variant.mediaId) this.selectMedia(variant.mediaId);
      if (updateURL) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
    updatePrice() {
      const variant = this.selectedVariant;
      if (!variant) return;
      const plan = variant.plans.find(item => String(item.id) === this.planInput?.value);
      const price = plan || variant;
      const onSale = Number(price.compare) > Number(price.price);
      this.querySelector('[data-price]').textContent = price.priceText;
      this.querySelector('[data-sticky-price]')?.replaceChildren(document.createTextNode(price.priceText));
      const compare = this.querySelector('[data-compare]');
      compare.hidden = !onSale; compare.textContent = price.compareText;
      const saving = this.querySelector('[data-saving]');
      saving.hidden = !onSale; saving.textContent = onSale ? `−${Math.round((price.compare - price.price) * 100 / price.compare)}%` : '';
      const unit = this.querySelector('[data-unit-price]');
      unit.hidden = !variant.unitText || !!plan; unit.textContent = variant.unitText || '';
      const unavailablePlan = this.planInput && ((this.dataset.requiresSellingPlan === 'true' && !plan) || (this.planInput.value && !plan));
      this.available = !!variant.available && !unavailablePlan;
      this.querySelectorAll('[data-add], [data-sticky-add]').forEach(button => { button.disabled = !this.available || this.busy; });
      this.querySelectorAll('[data-add-label], [data-sticky-label]').forEach(label => { label.textContent = this.available ? strings().add : strings().sold; });
    }
    setError(message) { if (this.error) { this.error.textContent = message; this.error.hidden = !message; } }
    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.available || !this.form.reportValidity()) return;
      this.busy = true; this.setError(''); this.status.textContent = '';
      this.querySelectorAll('[data-add], [data-sticky-add]').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      const drawer = document.querySelector('cart-drawer');
      const body = new FormData(this.form);
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.set('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
        body.set('sections_url', window.location.pathname);
        drawer.setActiveElement?.(event.submitter || this.querySelector('[data-add]'));
      }
      try {
        const response = await fetch(route('cart_add_url', 'cart/add.js'), { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Das Produkt konnte nicht hinzugefügt werden. Bitte prüfe die gewählte Menge.');
        this.status.textContent = 'Zum Warenkorb hinzugefügt.';
        const canRender = drawer && typeof drawer.renderContents === 'function' && result.sections?.['cart-drawer'] && result.sections?.['cart-icon-bubble'];
        if (canRender) {
          try {
            drawer.classList.remove('is-empty');
            drawer.renderContents(result);
            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'ac-product', productVariantId: body.get('id'), cartData: result });
          } catch { window.location.assign(route('cart_url', 'cart')); }
        } else { window.location.assign(route('cart_url', 'cart')); }
      } catch (error) {
        this.setError(error instanceof TypeError ? 'Die Verbindung wurde unterbrochen. Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst.' : error.message);
      } finally {
        this.busy = false;
        this.querySelectorAll('[data-add], [data-sticky-add]').forEach(button => button.removeAttribute('aria-busy'));
        this.updatePrice();
      }
    }
  }
  if (!customElements.get('ac-header')) customElements.define('ac-header', AcHeader);
  if (!customElements.get('ac-product')) customElements.define('ac-product', AcProduct);
})();
